// ============================================================================
// CHANGO V5.0 — Material Categories Manager (Task #119 / Round 12)
// ============================================================================
// Manage material categories (物料大类) — list, create, edit, archive.
// Mount: /app/factory/material-categories
//
// Features:
//   - List all un-archived categories with color preview
//   - Create new category (modal: code + name + color + sort_order)
//   - Inline edit name + color (code immutable)
//   - Soft archive (reject if items still in category)
//   - boss/finance only (sales denied)
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import RoleGate from '../../components/RoleGate.jsx';

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export default function MaterialCategoriesManager() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Modal form
  const [form, setForm] = useState({
    code: '',
    name: '',
    color: '#3b82f6',
    sort_order: 0
  });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/material-categories');
        setCategories(res?.items || []);
      } catch (err) {
        if (!(err instanceof HttpError && err.status === 404)) {
          setError(err.message || 'Failed to load categories');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Check sales role
  if (isSales) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          权限不足：仅 boss/finance 可访问工厂管理页面
        </div>
      </div>
    );
  }

  const handleCreateClick = () => {
    setForm({ code: '', name: '', color: '#3b82f6', sort_order: 0 });
    setEditingId(null);
    setShowModal(true);
  };

  const handleEditClick = (cat) => {
    setForm({
      code: cat.code,
      name: cat.name,
      color: cat.color || '#3b82f6',
      sort_order: cat.sort_order ?? 0
    });
    setEditingId(cat.id);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setForm({ code: '', name: '', color: '#3b82f6', sort_order: 0 });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('Category name is required');
      return;
    }

    if (!editingId && !form.code.trim()) {
      setError('Category code is required');
      return;
    }

    if (!HEX_COLOR_PATTERN.test(form.color)) {
      setError('Color must be #RRGGBB format');
      return;
    }

    try {
      setError(null);
      if (editingId) {
        // PATCH: update name/color/sort_order only (code immutable)
        await api.patch(`/api/material-categories/${editingId}`, {
          name: form.name.trim(),
          color: form.color,
          sort_order: Number(form.sort_order) || 0
        });
      } else {
        // POST: create new category
        await api.post('/api/material-categories', {
          code: form.code.trim(),
          name: form.name.trim(),
          color: form.color,
          sort_order: Number(form.sort_order) || 0
        });
      }
      // Refresh list
      const res = await api.get('/api/material-categories');
      setCategories(res?.items || []);
      handleModalClose();
    } catch (err) {
      setError(err.message || 'Failed to save category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this category? (Items must be moved first)')) {
      return;
    }

    try {
      await api.delete(`/api/material-categories/${id}`);
      setCategories(categories.filter((c) => c.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to archive category');
    }
  };

  return (
    <div className="px-6 py-6 max-w-4xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">物料大类</h1>
          <p className="text-sm text-slate-500 mt-1">
            管理物料分类（电气件、机械件、原料等）
          </p>
        </div>
        <button
          onClick={handleCreateClick}
          className="btn-primary"
        >
          + 新增大类
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading && (
          <div className="px-4 py-8 text-center text-slate-400">Loading...</div>
        )}

        {!loading && categories.length === 0 && (
          <div className="px-4 py-8 text-center text-slate-400">
            No categories yet · <button onClick={handleCreateClick} className="text-chango-700 hover:underline">Create one</button>
          </div>
        )}

        {!loading && categories.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Color</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{cat.code}</td>
                  <td className="px-4 py-3">{cat.name}</td>
                  <td className="px-4 py-3">
                    {cat.color && (
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-6 w-6 rounded border border-slate-300"
                          style={{ backgroundColor: cat.color }}
                        />
                        <code className="text-xs text-slate-500">{cat.color}</code>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleEditClick(cat)}
                      className="text-xs text-chango-700 hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(cat.id)}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      Archive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4">
        <Link to="/app/dashboard" className="text-sm text-slate-600 hover:underline">
          Back to Dashboard
        </Link>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? 'Edit Category' : 'Create New Category'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              {!editingId && (
                <label className="text-xs block">
                  Code (Business Key) *
                  <input
                    type="text"
                    className="field mt-1"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g., 电气, mech-parts"
                    maxLength="40"
                  />
                </label>
              )}

              <label className="text-xs block">
                Name *
                <input
                  type="text"
                  className="field mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Electrical Components"
                  maxLength="60"
                />
              </label>

              <label className="text-xs block">
                Color
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    className="h-10 w-14 cursor-pointer"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="field flex-1"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="#3b82f6"
                    maxLength="7"
                  />
                </div>
              </label>

              <label className="text-xs block">
                Sort Order
                <input
                  type="number"
                  className="field mt-1"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  placeholder="0"
                />
              </label>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn-ghost text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary text-sm"
                >
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
